import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Filter } from "lucide-react";
import { cn } from "../../utils/cn";

interface MobileActionFABProps {
    onAdd: () => void;
    onFilter: () => void;
    activeFilterCount: number;
}

export function MobileActionFAB({ onAdd, onFilter, activeFilterCount }: MobileActionFABProps) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <AnimatePresence>
                {isOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
                            onClick={() => setIsOpen(false)}
                        />
                        <div className="fixed right-4 bottom-40 z-50 flex flex-col items-end space-y-3">
                            <motion.button
                                initial={{ opacity: 0, y: 10, scale: 0.8 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 10, scale: 0.8 }}
                                transition={{ delay: 0.05 }}
                                onClick={() => { onAdd(); setIsOpen(false); }}
                                className="flex items-center gap-3 pr-2"
                            >
                                <span className="bg-white text-siemens-deep-blue text-sm font-bold py-1.5 px-3 rounded-lg shadow-lg">
                                    Create New Epic
                                </span>
                                <div className="w-12 h-12 rounded-full bg-siemens-petrol text-white shadow-xl flex items-center justify-center">
                                    <Plus className="w-6 h-6" />
                                </div>
                            </motion.button>

                            <motion.button
                                initial={{ opacity: 0, y: 10, scale: 0.8 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 10, scale: 0.8 }}
                                onClick={() => { onFilter(); setIsOpen(false); }}
                                className="flex items-center gap-3 pr-2"
                            >
                                <span className="bg-white text-siemens-deep-blue text-sm font-bold py-1.5 px-3 rounded-lg shadow-lg">
                                    Filter Epics
                                </span>
                                <div className="w-12 h-12 rounded-full bg-siemens-deep-blue text-white shadow-xl flex items-center justify-center relative">
                                    <Filter className="w-5 h-5" />
                                    {activeFilterCount > 0 && (
                                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-siemens-feedback-orange text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-siemens-deep-blue">
                                            {activeFilterCount}
                                        </span>
                                    )}
                                </div>
                            </motion.button>
                        </div>
                    </>
                )}
            </AnimatePresence>

            <motion.button
                initial={{ scale: 0 }}
                animate={{ scale: 1, rotate: isOpen ? 45 : 0 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    "fixed right-4 bottom-24 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-colors border-2 border-white/10",
                    isOpen ? "bg-siemens-deep-blue text-white" : "bg-siemens-petrol text-white hover:bg-siemens-petrol-dark"
                )}
            >
                <Plus className="w-7 h-7" />
                {activeFilterCount > 0 && !isOpen && (
                    <span className="absolute top-0 right-0 w-4 h-4 bg-siemens-feedback-orange rounded-full border-2 border-siemens-petrol" />
                )}
            </motion.button>
        </>
    );
}

export default MobileActionFAB;
